import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory, Link } from "react-router-dom";
import "./style/Dashboard.css";

import { AiOutlineMail } from "react-icons/ai";
import {
  MdLocalShipping,
  MdOutlinePersonOff,
  MdHelpOutline,
  MdPreview,
  MdScience,
  MdOutlineForum,
  MdOutlineCancelScheduleSend
} from "react-icons/md";
import { LuCalendarClock } from "react-icons/lu";
import { BsSearch, BsArrowRight } from "react-icons/bs";
import { Form } from "react-bootstrap";
import { debounce } from "lodash";

import DashboardSingleCard from "./DashboardSingleCard";
import SwitchButton from "./SwitchButton";

import { getMessageCount, getBookingByDate, getKitByStatus, getOrderByStatus, getTestResultWithStatus } from "./helper/apiCaller";
import { BookingColumns, KIT_COLUMNS, TEST_RESULT_COLUMNS, ORDER_PENDING_SHIPMENT_COLUMNS } from "./helper/tableColoumns";
import { AVAILABLE_STATES_FULL } from "./helper/constant";
import { MultipleSelectAutocomplete } from "./MultipleSelectAutocomplete";
import { clinicianIdFromRefreshAuth } from "../../util/authUtil";
import ApiService from "../../ApiService";

const FILTER_OPTIONS = [
  { value: "All", label: "All" },
  { value: "Online", label: "Online" },
  { value: "2101 Larimer St, Denver", label: "Denver Care Hub @ 2101 Larimer St, Denver" },
  { value: "8 E Charleston Blvd, Las Vegas", label: "Las Vegas Care Hub @ 8 E Charleston Blvd" }
];

export default function Dashboard() {
  let navigate = useHistory();
  const [activeCard, setActiveCard] = useState("todaysAppointment");
  const [tableState, setTableState] = useState({
    columns: BookingColumns,
    data: [],
    heading: "Today's Appointment"
  });

  const [clinicianState, setClinicianState] = useState("");
  useEffect(() => {
    clinicianIdFromRefreshAuth().then(id => {
      ApiService.getClinicianProfile(id)
        .then(res => {
          if (res.status === 200) {
            setClinicianState(res.data?.state);
          }
        })
        .catch(err => {
          console.log(err);
        });
    });
  }, []);

  const [messageCount, setMessageCount] = useState(0);
  const [todaysBooking, setTodaysBooking] = useState([]);
  const [kitWithRejectedSamples, setKitWithRejectedSamples] = useState([]);
  const [orderPendingShipment, setOrderPendingShipment] = useState([]);
  const [testResultWithNoPatients, setTestResultWithNoPatients] = useState([]);
  const [testResultsWithInconclusiveResults, setTestResultsWithInconclusiveResults] = useState([]);
  const [testResultsNeedingReview, setTestResultsNeedingReview] = useState([]);
  const [testResultsPendingConsultation, setTestResultsPendingConsultation] = useState([]);
  const [labOrderPendingResults, setLabOrderPendingResults] = useState([]);

  const [filter, setFilter] = useState("All");
  const [stateFilter, setStateFilter] = useState([{ id: "All", title: "All" }]);
  const [switchStateFilter, setSwitchStateFilter] = useState("myStates");

  useEffect(() => {
    getMessageCount(res => {
      if (res.success) setMessageCount(res.data);
    });
    const date = new Date().toISOString().split("T")[0];
    getBookingByDate(date, res => {
      if (res.success) {
        setTodaysBooking(res.data);
        setTableState(prevState => ({ ...prevState, data: res.data, heading: "Today's Appointment" }));
      }
    });
    getKitByStatus("REJECTED", res => {
      if (res.success) setKitWithRejectedSamples(res.data);
    });
    getKitByStatus("PENDING_RESULTS", res => {
      if (res.success) setLabOrderPendingResults(res.data);
    });
    getOrderByStatus("PENDING_SHIPMENT", res => {
      if (res.success) setOrderPendingShipment(res.data);
    });
    getTestResultWithStatus("NO_PATIENT", res => {
      if (res.success) setTestResultWithNoPatients(res.data);
    });
    getTestResultWithStatus("INCONCLUSIVE", res => {
      if (res.success) setTestResultsWithInconclusiveResults(res.data);
    });
    getTestResultWithStatus("NEEDS_CLINICIAN_REVIEW", res => {
      if (res.success) setTestResultsNeedingReview(res.data);
    });
    getTestResultWithStatus("NEEDS_CONSULT", res => {
      if (res.success) setTestResultsPendingConsultation(res.data);
    });
  }, []);

  // Tier 1: Primary clinical action
  const tier1Cards = [
    { heading: "Today's Appointments", type: "todaysAppointment", count: todaysBooking?.length, IconComponent: <LuCalendarClock />, isFocus: false },
    { heading: "Results Needing Review", type: "testResultsNeedingReview", count: testResultsNeedingReview?.length, IconComponent: <MdPreview />, isFocus: false },
    { heading: "Pending Consultation", type: "testResultsPendingConsultation", count: testResultsPendingConsultation?.length, IconComponent: <MdOutlineForum />, isFocus: true },
  ];

  // Tier 2: Operational metrics
  const tier2Cards = [
    { heading: "Orders Pending Shipment", type: "orderPendingShipment", count: orderPendingShipment?.length, IconComponent: <MdLocalShipping />, isFocus: true },
    { heading: "Rejected Samples", type: "rejectedSamples", count: kitWithRejectedSamples?.length, IconComponent: <MdOutlineCancelScheduleSend />, isFocus: false },
    { heading: "Lab Orders Pending", type: "labOrderPendingResults", count: labOrderPendingResults?.length, IconComponent: <MdScience />, isFocus: true },
    { heading: "Inconclusive Results", type: "testResultsWithInconclusiveResults", count: testResultsWithInconclusiveResults?.length, IconComponent: <MdHelpOutline />, isFocus: false },
    { heading: "Results — No Patient", type: "testResultsWithNoPatients", count: testResultWithNoPatients?.length, IconComponent: <MdOutlinePersonOff />, isFocus: true },
  ];

  // Tier 3: Informational
  const tier3Cards = [
    { heading: "Unread Messages", type: "unreadMessages", count: messageCount, IconComponent: <AiOutlineMail />, isFocus: false },
  ];

  const debouncedChangeCard = useCallback(
    debounce(newState => { setTableState(newState); }, 300),
    []
  );

  const handleCardClick = useCallback(
    type => {
      if (activeCard === type) return;
      if (type === "unreadMessages") { navigate.push("/inbox"); return; }
      let newState = { columns: BookingColumns, data: [], heading: "" };
      if (type === "todaysAppointment") { newState.data = todaysBooking; newState.columns = BookingColumns; newState.heading = "Today's Appointment"; }
      else if (type === "rejectedSamples") { newState.data = kitWithRejectedSamples; newState.columns = KIT_COLUMNS; newState.heading = "Rejected Samples"; }
      else if (type === "orderPendingShipment") { newState.data = orderPendingShipment; newState.columns = ORDER_PENDING_SHIPMENT_COLUMNS; newState.heading = "Order Pending Shipment"; }
      else if (type === "testResultsWithNoPatients") { newState.data = testResultWithNoPatients; newState.columns = TEST_RESULT_COLUMNS; newState.heading = "Test Results pending patient register"; }
      else if (type === "testResultsWithInconclusiveResults") { newState.data = testResultsWithInconclusiveResults; newState.columns = TEST_RESULT_COLUMNS; newState.heading = "Test Results With Inconclusive Results"; }
      else if (type === "testResultsNeedingReview") { newState.data = testResultsNeedingReview; newState.columns = TEST_RESULT_COLUMNS; newState.heading = "Test Results Need Review"; }
      else if (type === "testResultsPendingConsultation") { newState.data = testResultsPendingConsultation; newState.columns = TEST_RESULT_COLUMNS; newState.heading = "Test Results Pending Consultation"; }
      else if (type === "labOrderPendingResults") { newState.data = labOrderPendingResults; newState.columns = KIT_COLUMNS; newState.heading = "Lab Order Pending Results"; }
      setActiveCard(type);
      debouncedChangeCard(newState);
    },
    [activeCard, debouncedChangeCard, todaysBooking, kitWithRejectedSamples, orderPendingShipment, testResultWithNoPatients, testResultsWithInconclusiveResults, testResultsNeedingReview, testResultsPendingConsultation, labOrderPendingResults]
  );

  const handleRowClick = row => {
    if (activeCard === "todaysAppointment") { window.open(`/BookingDetails?bookingId=${row.bookingId}`, '_blank'); }
    if (activeCard === "rejectedSamples") { window.open(`KitDetails?kitId=${row.kitId}`, '_blank'); }
    if (activeCard === "testResultsWithNoPatients") { window.open(`KitDetails?kitId=${row.kitId}`, '_blank'); }
    if (activeCard === "testResultsWithInconclusiveResults") { window.open(`KitDetails?kitId=${row.kitId}`, '_blank'); }
    if (activeCard === "testResultsNeedingReview") { window.open(`KitDetails?kitId=${row.kitId}`, '_blank'); }
    if (activeCard === "testResultsPendingConsultation") { window.open(`KitDetails?kitId=${row.kitId}`, '_blank'); }
    if (activeCard === "labOrderPendingResults") { window.open(`KitDetails?kitId=${row.kitId}`, '_blank'); }
  };

  const filteredData = useMemo(() => {
    let data = tableState.data;
    if (filter === "Online") { data = data.filter(item => item?.careLocation === "Online"); }
    else if (filter !== "All") { data = data.filter(item => item?.careLocation === filter); }
    if (switchStateFilter === "myStates") {
      const filterProperty = activeCard === "todaysAppointment" ? "careStateRegion" : "careLocation";
      data = data.filter(item => item[filterProperty] === clinicianState);
    } else if (switchStateFilter === "allStates" && stateFilter.length > 0 && stateFilter[0].title !== "All") {
      const stateTitles = stateFilter.map(state => state.title);
      data = data.filter(item => stateTitles.includes(item?.careStateRegion));
    }
    return data;
  }, [filter, activeCard, clinicianState, stateFilter, switchStateFilter, tableState.data]);

  const renderCardGroup = (cards) =>
    cards.map(card => (
      <DashboardSingleCard
        key={card.heading}
        heading={card.heading}
        count={card.count}
        IconComponent={card.IconComponent}
        isActive={activeCard === card.type}
        onClick={() => handleCardClick(card.type)}
        isAttentionRequired={card.isFocus && card.count > 0}
        isZero={card.count === 0}
      />
    ));

  return (
    <div className="db-root">
      {/* Quick Patient Lookup — top row */}
      <div className="db-quick-row">
        <Link to="/SearchPatient" className="db-quick-search" aria-label="Go to Patient Search">
          <div className="db-quick-search__left">
            <span className="db-quick-search__icon"><BsSearch size={18} /></span>
            <div>
              <span className="db-quick-search__title">Patient Search</span>
              <span className="db-quick-search__desc">Look up patients by name, email, phone, or ID</span>
            </div>
          </div>
          <BsArrowRight size={18} className="db-quick-search__arrow" />
        </Link>
      </div>

      {/* Overview header + filter controls */}
      <div className="db-section-header">
        <h1 className="db-page-title">Overview</h1>
        <div className="db-filter-group">
          <span className="db-filter-group__label">Viewing</span>
          <div className="db-filter-group__controls">
            <Form.Control
              as="select"
              size="sm"
              className="db-filter-select"
              value={filter}
              onChange={e => setFilter(e.target.value)}
            >
              {FILTER_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Form.Control>
            <SwitchButton setValue={setSwitchStateFilter} value={switchStateFilter} />
            {switchStateFilter !== "myStates" && (
              <MultipleSelectAutocomplete mapOptions={AVAILABLE_STATES_FULL} setValue={setStateFilter} value={stateFilter} />
            )}
          </div>
        </div>
      </div>

      {/* ── Tier 1: Clinical Action Required ── */}
      <div className="db-tier">
        <h2 className="db-tier__label">Clinical</h2>
        <div className="db-cards-grid db-cards-grid--t1">
          {renderCardGroup(tier1Cards)}
        </div>
      </div>

      {/* ── Tier 2: Operations ── */}
      <div className="db-tier">
        <h2 className="db-tier__label">Operations</h2>
        <div className="db-cards-grid db-cards-grid--t2">
          {renderCardGroup(tier2Cards)}
        </div>
      </div>

      {/* ── Tier 3: Informational ── */}
      <div className="db-tier db-tier--last">
        <h2 className="db-tier__label">Inbox</h2>
        <div className="db-cards-grid db-cards-grid--t3">
          {renderCardGroup(tier3Cards)}
        </div>
      </div>

      {/* ── Data table ── */}
      <div className="db-table-section">
        <div className="db-table-header">
          <h2 className="db-table-title">{tableState.heading}</h2>
          <span className="db-table-count">{filteredData.length} record{filteredData.length !== 1 ? "s" : ""}</span>
        </div>
        <div className="db-table-wrap">
          {filteredData.length === 0 ? (
            <div className="db-table-empty">
              <p className="db-table-empty__title">No data to display</p>
              <p className="db-table-empty__text">Select a different metric card above, or adjust your filters.</p>
              <div className="db-table-empty__actions">
                <Link to="/BookingList" className="db-table-empty__link">View all appointments</Link>
                <button type="button" className="db-table-empty__link" onClick={() => { setFilter("All"); setSwitchStateFilter("myStates"); }}>Reset filters</button>
              </div>
            </div>
          ) : (
            <table className="db-table">
              <thead>
                <tr>
                  {tableState.columns.map(column => (
                    <th key={column.title}>{column.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row, index) => (
                  <tr key={index}>
                    {tableState.columns.map(column => (
                      <td key={column.field}>
                        {column?.isLink ? (
                          <span className="db-table-link" onClick={() => handleRowClick(row)}>{row[column.field]}</span>
                        ) : (
                          row[column.field]
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
